import { BASE_PRECISION_EXP, BigNum, ZERO } from '@velocity-exchange/sdk';
import { MarketId } from '@velocity-exchange/common';
import { memo, useMemo } from 'react';
import StatDisplay from './StatDisplay';
import SkeletonValuePlaceholder from '../SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { cj } from 'src/utils/classNames';
import {
	MARKET_STATS_BAR_CELL_VALUE_SKELETON_CLASSNAME,
	MARKET_STATS_BAR_CELL_LABEL_CLASSNAME,
} from 'src/components/MarketStatsBar/consts';
import useVelocityClient from 'src/hooks/useVelocityClient';

interface Props {
	symbol: string;
	maxOpenInterest: string;
	longOpenInterest: string;
	shortOpenInterest: string;
	openInterest: string;
	oraclePrice: BigNum;
	marketId: MarketId;
}

function OpenInterestCard({
	symbol,
	maxOpenInterest,
	longOpenInterest,
	shortOpenInterest,
	openInterest,
	oraclePrice,
	marketId,
}: Props) {
	const isMobileScreenSize = useIsMobileScreenSize();
	const velocityClient = useVelocityClient();
	const isSubscribed = velocityClient?.isSubscribed;
	const perpMarketAccount = useMemo(
		() => velocityClient?.getPerpMarketAccount(marketId.marketIndex),
		[marketId.marketIndex, isSubscribed] // need to pass in isSubscribed because account will be empty beforehand
	);

	const openInterestBN = BigNum.from(
		perpMarketAccount?.baseAssetAmountLong.add(
			perpMarketAccount?.baseAssetAmountShort.abs()
		) ?? ZERO,
		BASE_PRECISION_EXP
	);
	const openInterestQuote = openInterestBN.mul(oraclePrice).toMillified();

	return (
		<Tooltip
			decoration="cursor-help"
			content={
				<div className="flex flex-col gap-y-md">
					<div className="flex flex-col gap-y-sm">
						<div className="flex justify-between">
							<span>
								<span className="text-text-positive-green-button">LONG</span> OI
							</span>
							<span className="text-text-default">
								{longOpenInterest} {symbol}
							</span>
						</div>
						<div className="flex justify-between">
							<span>
								<span className="text-text-negative-red-button">SHORT</span> OI
							</span>
							<span className="text-text-default">
								{shortOpenInterest} {symbol}
							</span>
						</div>
						<div className="flex justify-between">
							<span>Max OI (Long or Short)</span>
							<span className="text-text-default">
								{maxOpenInterest} {symbol}
							</span>
						</div>
						Open Interest (OI) is the total size of all open positions.
					</div>
				</div>
			}
		>
			<div className="flex cursor-help flex-col">
				<StatDisplay
					header="Open Interest"
					headerClassName={MARKET_STATS_BAR_CELL_LABEL_CLASSNAME}
				>
					{openInterest !== '0' ? (
						<div
							className={cj(
								'flex shrink-0 items-center justify-start space-x-1 text-text-default',
								isMobileScreenSize && 'max-w-20'
							)}
						>
							<span className={isMobileScreenSize ? 'truncate' : undefined}>
								${openInterestQuote}
							</span>
						</div>
					) : (
						<SkeletonValuePlaceholder
							className={MARKET_STATS_BAR_CELL_VALUE_SKELETON_CLASSNAME}
							loading
						/>
					)}
				</StatDisplay>
			</div>
		</Tooltip>
	);
}

export default memo(OpenInterestCard);
